import React, { useEffect, useRef } from 'react';

/**
 * Componente de mapa usando Leaflet + OpenStreetMap (100% gratuito, sin API key).
 * Muestra los fichajes con ubicación como marcadores de colores y opcionalmente
 * la posición y el margen de tolerancia de la empresa.
 */
function MapaFichajes({ fichajes, settings }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef([]);

  // Inicializar mapa una sola vez
  useEffect(() => {
    if (mapInstanceRef.current) return;
    // Importación dinámica de Leaflet para evitar SSR issues
    import('leaflet').then(L => {
      L = L.default || L;

      // Asegurar que los iconos de Leaflet funcionen correctamente con CRA
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
        iconUrl: require('leaflet/dist/images/marker-icon.png'),
        shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
      });

      if (!mapRef.current) return;
      // Eliminar marca de Leaflet si el div fue reutilizado (ej. StrictMode o re-apertura de modal)
      if (mapRef.current._leaflet_id) {
        delete mapRef.current._leaflet_id;
      }
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19
      }).addTo(map);
      mapInstanceRef.current = map;
      mapInstanceRef.current._leaflet = L;
      updateLayers(map, L, fichajes, settings);
    });
    // eslint-disable-next-line
  }, []);

  // Actualizar markers cuando cambian fichajes o settings
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const L = map._leaflet;
    if (!L) return;
    updateLayers(map, L, fichajes, settings);
  }, [fichajes, settings]);

  function updateLayers(map, L, fichajes, settings) {
    // Limpiar capas anteriores
    layersRef.current.forEach(l => l.remove());
    layersRef.current = [];

    const bounds = [];

    // Marcador y círculo de la empresa
    if (settings && settings.latitud != null && settings.longitud != null) {
      const empLatLng = [settings.latitud, settings.longitud];

      const circle = L.circle(empLatLng, {
        radius: settings.margenMetros || 200,
        color: '#1976d2',
        fillColor: '#1976d2',
        fillOpacity: 0.08,
        weight: 2,
        dashArray: '6 4'
      }).addTo(map);
      layersRef.current.push(circle);

      const empIcon = L.divIcon({
        className: '',
        html: `<div style="
          width:38px;height:38px;border-radius:50%;
          background:linear-gradient(135deg,#1976d2,#42a5f5);
          border:3px solid #fff;
          box-shadow:0 2px 10px rgba(25,118,210,0.5);
          display:flex;align-items:center;justify-content:center;
          font-size:18px;line-height:1;
        ">🏢</div>`,
        iconSize: [38, 38],
        iconAnchor: [19, 19],
        popupAnchor: [0, -20]
      });
      const empMarker = L.marker(empLatLng, { icon: empIcon })
        .addTo(map)
        .bindPopup(`<b>🏢 Empresa</b><br/>${settings.direccionEmpresa || ''}<br/><small>Margen: ${settings.margenMetros || 200}m</small>`);
      layersRef.current.push(empMarker);
      bounds.push(empLatLng);
    }

    // Marcadores de fichajes con ubicación válida
    const conUbicacion = fichajes.filter(
      f => f.ubicacion && f.ubicacion.latitud != null && f.ubicacion.longitud != null && !f.ubicacion.error
    );

    conUbicacion.forEach(f => {
      const lat = f.ubicacion.latitud;
      const lon = f.ubicacion.longitud;
      const esEntrada = f.tipo === 'entrada';
      const dentroMargen = f.ubicacion.dentroDelMargen;
      const color = esEntrada ? '#4caf50' : '#f44336';
      const emoji = esEntrada ? '🟢' : '🔴';

      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:32px;height:32px;border-radius:50%;
          background:${color};
          border:3px solid #fff;
          box-shadow:0 2px 8px ${color}80;
          display:flex;align-items:center;justify-content:center;
          font-size:14px;line-height:1;
          ${dentroMargen === false ? 'outline: 3px solid #f59e0b;' : ''}
        ">${emoji}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -18]
      });

      const hora = new Date(f.fecha).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const fecha = new Date(f.fecha).toLocaleDateString('es-ES', { weekday:'short', day:'2-digit', month:'short', year:'numeric' });
      const nombre = `${f.usuario?.nombre || ''} ${f.usuario?.apellidos || ''}`.trim();
      const distM = f.ubicacion.distanciaEmpresa;
      const distStr = distM != null
        ? (distM >= 1000 ? `${(distM/1000).toFixed(2)} km` : `${distM} m`)
        : null;
      const margenStr = distStr
        ? (f.ubicacion.dentroDelMargen ? `✅ ${distStr} · dentro del margen` : `⚠️ ${distStr} · fuera del margen`)
        : '';
      const precisionStr = f.ubicacion.precision != null ? `±${Math.round(f.ubicacion.precision)} m` : '-';
      const ipStr = f.dispositivo?.ip || '-';
      const soStr = f.dispositivo?.so || '-';
      const duracionStr = f.tipo === 'salida' && typeof f.duracion === 'number'
        ? `${Math.floor(Math.abs(f.duracion)/60)}h ${Math.abs(f.duracion)%60}min`
        : null;

      const marker = L.marker([lat, lon], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="font-family:Quicksand,sans-serif;min-width:200px;max-width:280px;line-height:1.6">
            <div style="font-weight:800;font-size:1.05em;margin-bottom:6px;border-bottom:2px solid #e2e8f0;padding-bottom:4px">
              ${emoji} ${esEntrada ? 'Entrada' : 'Salida'}
            </div>
            <div><strong>👤</strong> ${nombre || '-'}</div>
            <div><strong>🕐</strong> ${hora} · ${fecha}</div>
            ${duracionStr ? `<div><strong>⏱️</strong> Duración: ${duracionStr}</div>` : ''}
            ${margenStr ? `<div style="margin-top:4px">${margenStr}</div>` : ''}
            <div><strong>📍</strong> Precisión GPS: ${precisionStr}</div>
            <div><strong>🖥️</strong> SO: ${soStr}</div>
            <div><strong>🌐</strong> IP: ${ipStr}</div>
            ${f.registro_desconocido ? `<div style="color:#f57c00;font-weight:700;margin-top:4px">⚠️ Dispositivo diferente al habitual</div>` : ''}
          </div>
        `, { maxWidth: 300 });
      layersRef.current.push(marker);
      bounds.push([lat, lon]);
    });

    if (bounds.length > 0) {
      try {
        map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 16 });
      } catch (_) {}
    } else if (settings && settings.latitud != null) {
      map.setView([settings.latitud, settings.longitud], 14);
    } else {
      map.setView([40.4168, -3.7038], 6); // España por defecto
    }

    // Forzar redibujado por si el contenedor estaba oculto
    setTimeout(() => { try { map.invalidateSize(); } catch (_) {} }, 100);
  }

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch (_) {}
        mapInstanceRef.current = null;
      }
      // Limpiar la marca que Leaflet deja en el div para que pueda reiniciarse
      if (mapRef.current) {
        delete mapRef.current._leaflet_id;
      }
    };
  }, []);

  const conUbicacion = fichajes.filter(
    f => f.ubicacion && f.ubicacion.latitud != null && !f.ubicacion.error
  );
  const sinUbicacion = fichajes.length - conUbicacion.length;

  return (
    <div style={{ fontFamily: 'Quicksand, Montserrat, Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Mapa */}
      <div
        ref={mapRef}
        style={{
          flex: 1,
          width: '100%',
          minHeight: 'clamp(280px, 45vw, 500px)',
          borderRadius: 0,
          overflow: 'hidden',
          zIndex: 0
        }}
      />

      {conUbicacion.length === 0 && (
        <div style={{
          textAlign: 'center', color: '#94a3b8', padding: '1em',
          fontSize: '0.9em', fontStyle: 'italic'
        }}>
          Ningún fichaje del listado tiene coordenadas GPS
        </div>
      )}
    </div>
  );
}

export default MapaFichajes;
